import { Box, ChakraProps, Heading, Stack, Text, VStack } from "@biblioteksentralen/react";
import { ResolvedDigitalLibraryServiceSummary, ResolvedPaletteColor } from "@libry-content/common";
import { useId } from "react";
import { Translate, useTranslation } from "../../../utils/hooks/useTranslation";
import { useSitePalette } from "../../../utils/useSitePalette";
import { DefaultContainer } from "../../ContentContainer";
import { ArrowRight } from "react-feather";
import InternalLink from "../../InternalLink";
import { sift, unique } from "radash";
import { ResolvedLibrarySummary } from "../../library/sanityQuery";

type Props = {
  heading: string;
  teaser: string;
  link: string;
  paletteColor: ResolvedPaletteColor;
} & ChakraProps;

const ServicesAndDigitalLibraryCommonLink = ({ heading, teaser, link, paletteColor, ...props }: Props) => {
  return (
    <InternalLink
      href={link}
      display="flex"
      gap="1rem"
      alignItems="center"
      justifyContent="space-between"
      padding="2rem"
      borderRadius="md"
      transition="ease 0.2s"
      textDecoration="none"
      role="group"
      _hover={{
        bgGradient: "linear(to-r, blackAlpha.50 0%, blackAlpha.50 100%)",
      }}
      {...paletteColor}
      {...props}
    >
      <VStack alignItems="flex-start">
        <Heading as="h2" size="md" _groupHover={{ textDecoration: "underline" }}>
          {heading}
        </Heading>
        <Text>{teaser}</Text>
      </VStack>
      <Box margin="2rem 1rem" transition="ease 0.2s" _groupHover={{ transform: "translate(1rem, 0)" }}>
        <ArrowRight size="2.5rem" />
      </Box>
    </InternalLink>
  );
};

export const ServicesLink = (props: { library?: ResolvedLibrarySummary } & ChakraProps) => {
  const { t } = useTranslation();
  const palette = useSitePalette();

  return (
    <ServicesAndDigitalLibraryCommonLink
      heading={t("I biblioteket")}
      teaser={t("Oppdag bibliotekets tjenester")}
      link={`/tjenester${props.library ? `?libraries=["${props.library.slug}"]` : ""}`}
      paletteColor={palette.colors.lightaccent3}
      {...props}
    />
  );
};

export const DigitalServicesLink = ({
  digitalLibraryServices,
  ...props
}: {
  digitalLibraryServices: ResolvedDigitalLibraryServiceSummary[];
} & ChakraProps) => {
  const { t } = useTranslation();
  const palette = useSitePalette();

  const activities = getDigitalLibraryServicesActivities(digitalLibraryServices, t);

  return (
    <ServicesAndDigitalLibraryCommonLink
      heading={t("Digitalt bibliotek")}
      teaser={t("{aktiviteter} og mye mer på nett", { aktiviteter: activities })}
      link={"/digitalt-bibliotek"}
      paletteColor={palette.colors.lightaccent1}
      {...props}
    />
  );
};

export const ServicesAndDigitalServicesBanners = ({
  heading,
  orientation,
  library,
  digitalLibraryServices,
}: {
  heading?: string;
  library?: ResolvedLibrarySummary;
  orientation?: "column" | "row";
  digitalLibraryServices: ResolvedDigitalLibraryServiceSummary[];
}) => {
  const headingId = useId();

  return (
    <DefaultContainer as="section" aria-labelledby={headingId}>
      {heading && (
        <Heading as="h2" id={headingId} marginBottom="2rem">
          {heading}
        </Heading>
      )}
      <Stack
        gap="1rem"
        alignItems="stretch"
        {...(orientation === "column" ? { flexDirection: "column" } : { flexDirection: { base: "column", md: "row" } })}
      >
        <ServicesLink flex={1} library={library} />
        <DigitalServicesLink flex={1} digitalLibraryServices={digitalLibraryServices} />
      </Stack>
    </DefaultContainer>
  );
};

const getDigitalLibraryServicesActivities = (
  digitalLibraryServices: ResolvedDigitalLibraryServiceSummary[],
  t: Translate["t"]
) => {
  const categories = unique(sift(digitalLibraryServices.map(({ category }) => category)));
  const hasEBooks = categories.includes("ebooks-and-audiobooks");
  const hasNewspapers = categories.includes("newspapers-and-magazines");
  const hasMovies = categories.includes("film-services");

  if (hasEBooks && hasNewspapers && hasMovies) return t("Les bøker og aviser, se film");
  if (hasEBooks && !hasNewspapers && !hasMovies) return t("Les bøker");
  if (!hasEBooks && hasNewspapers && !hasMovies) return t("Les aviser");
  if (!hasEBooks && !hasNewspapers && hasMovies) return t("Se film");
  if (hasEBooks && hasNewspapers && !hasMovies) return t("Les bøker og aviser");
  if (!hasEBooks && hasNewspapers && hasMovies) return t("Les aviser, se film");
  if (hasEBooks && !hasNewspapers && hasMovies) return t("Les bøker, se film");
  return "";
};
